import type { ComponentType } from "react";
import {
  AccountIcon,
  AppsIcon,
  HistoryIcon,
  HouseholdsIcon,
  ListIcon,
  MemoryIcon,
  PlanIcon,
  RosterIcon,
} from "./components/icons";

/**
 * The panel's sections — **one list, read by the nav and by the routes** (issue #301).
 *
 * Two lists would compile and would drift the first time a path was renamed, leaving a nav
 * entry that 404s inside its own app. The four family sections, their order, their labels and
 * their icons are #293's wireframes: *What Boone knows*, *What's on your plate*, *Who's here*,
 * *What's changed*.
 *
 * **The labels are the family's words rather than the schema's**, which is the same rule the
 * tools' `title` annotations follow (#220): a member reads "What's changed", not "the audit
 * trail", and certainly not `ACTION#`.
 *
 * **`waitingOn` is why a section is a placeholder rather than a screen.** #301 is the shell:
 * the nav, the switcher, the session and sign-out. Each screen is its own issue, and a nav
 * entry that led nowhere at all would be worse than one that says so.
 *
 * **Deliberately not here: the conversations the wireframes show below these four.** Hosting
 * the conversation is ADR 0021 decision 11 — it inverts the cost model and makes pricing a
 * blocker — so the sidebar carries the pages and nothing else until that is decided. The same
 * goes for *Plan and billing* in the account menu.
 */

export interface Section {
  /** The route tail under the scope — `/families/:familyId/memory`. */
  readonly path: string;
  readonly label: string;
  /**
   * The wireframes' icon for this section — the **component**, not a rendered element.
   * Calling one here would work (they are pure and hold no state) and would be the wrong
   * habit to leave in a list every future section copies from.
   */
  readonly Icon: ComponentType;
  /** What this section will show, in the family's terms. Rendered by the placeholder. */
  readonly blurb: string;
  /**
   * The issue that replaces the placeholder with the real screen — **absent means built**.
   *
   * It is here rather than in the copy on purpose: a family reading "coming in #302" learns
   * nothing, and a date is a promise. What the placeholder says instead is where they *can*
   * do this today.
   */
  readonly waitingOn?: number;
}

export const FAMILY_SECTIONS: readonly Section[] = [
  {
    path: "memory",
    label: "What Boone knows",
    Icon: MemoryIcon,
    blurb:
      "Everything you've asked Boone to remember about your family — to read, correct, or take back.",
  },
  {
    path: "plate",
    label: "What's on your plate",
    Icon: ListIcon,
    blurb: "Your family's list: what's open, what's done, and who has it.",
  },
  {
    path: "roster",
    label: "Who's here",
    Icon: RosterIcon,
    blurb: "Who is in your family, and anyone you've invited who hasn't joined yet.",
  },
  {
    path: "activity",
    label: "What's changed",
    Icon: HistoryIcon,
    blurb: "The record of what Boone wrote down, when, and who asked for it.",
  },
];

/** The heading above them (#293). */
export const FAMILY_SECTIONS_HEADING = "The family's pages";

/**
 * **Plan and billing — routable, and deliberately not in the nav** (issue #303).
 *
 * `Roster.dc.html` carries a row saying which plan the family is on and offering *Manage the
 * plan*, because the six-seat ceiling is the free tier's number rather than an arbitrary cap
 * (#251) — a family that meets it is entitled to know it is a plan they could change. The
 * canvas does not put the page in the sidebar, and neither does this: it is reached from the
 * one sentence that raises it.
 *
 * **`waitingOn` names the epic rather than an issue of its own, and that is honest rather than
 * lazy.** There is no billing issue, because ADR 0021 decision 11 makes pricing a *decision*
 * before it is a screen — the same reason `Plan and billing` is absent from the account menu
 * and the conversations are absent from the sidebar above. #293 is where that decision lands.
 */
export const PLAN_SECTION: Section = {
  path: "plan",
  label: "Plan and billing",
  Icon: PlanIcon,
  blurb: "The plan your family is on, and what it includes.",
  waitingOn: 293,
};

/**
 * The person scope's sections (ADR 0021 decision 2: connected apps and identities are about a
 * person, not a tenant).
 */
export const ACCOUNT_SECTION: Section = {
  path: "account",
  label: "Your account",
  Icon: AccountIcon,
  blurb: "The account this browser is signed in as.",
};

/**
 * **Your households** — the person scope's third page, and the panel's create (issue #360).
 *
 * It belongs here rather than under a family for the reason the whole scope exists: how many
 * households somebody is in, and starting another, are facts about a **person**. There is no
 * family to scope either by, and ADR 0022 decision 2a makes the number a bound on the person
 * too.
 *
 * **It is where a one-household member starts a second**, which the switcher deliberately
 * cannot be: at one household the switcher is a label rather than a menu, so the affordance
 * needs somewhere that exists whatever the count. The switcher links here once there are two.
 */
export const HOUSEHOLDS_SECTION: Section = {
  path: "households",
  label: "Your households",
  Icon: HouseholdsIcon,
  blurb: "The households you're part of, and a way to start another.",
};

/**
 * **Connected apps** — the person scope's third page, and the lever ADR 0021 calls a security
 * gap rather than a missing feature (issue #116, decision 9).
 *
 * The blurb is `ConnectedApps.dc.html`'s, and its second sentence is doing work the label
 * cannot: a grant is held by a **person** and spans every household they are in, so cutting an
 * app off is not an act within one family. Somebody who thought otherwise would expect this
 * page to behave like the roster, and would be wrong in the direction that matters.
 */
export const CONNECTED_APPS: Section = {
  path: "apps",
  label: "Connected apps",
  Icon: AppsIcon,
  blurb:
    "Apps you've allowed to reach Boone on your behalf. Cutting one off ends its access to every family you're in — access is yours, not a family's.",
};

export const PERSON_SECTIONS: readonly Section[] = [
  ACCOUNT_SECTION,
  HOUSEHOLDS_SECTION,
  CONNECTED_APPS,
];

/** The heading above those. */
export const PERSON_SECTIONS_HEADING = "You";
